import { useState } from "react";
import { categories, site } from "@/mocks/portal";

const FORM_URL = "https://readdy.ai/api/form/dae39h3ep053ijub17pg";

interface Props {
  id: string;
  withMessage?: boolean;
  title: string;
  subtitle?: string;
  tone?: "light" | "dark";
}

type Status = "idle" | "success" | "error";

export default function NewsletterForm({ id, withMessage = false, title, subtitle, tone = "light" }: Props) {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const honeypot = (data.get("company_alt") as string | null)?.trim() ?? "";
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }
    data.delete("company_alt");
    setStatus("idle");
    setFormError("");
    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(data as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || (responseText && responseText.trim()) || "";
      const isSpam =
        serverMsg.toLowerCase().includes("spam") || responseText.toLowerCase().includes("form data is spam");
      if (res.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setFormError(serverMsg || "提交失败，请稍后再试。");
        setStatus("error");
      }
    } catch {
      setFormError("网络似乎开小差了，请稍后重试。");
      setStatus("error");
    }
  }

  const dark = tone === "dark";
  return (
    <div id={id} className={`scroll-mt-24 ${dark ? "text-background-50" : "text-foreground-900"}`}>
      {(title || subtitle) && (
        <div>
          {title && (
            <h3 className={`font-heading text-xl font-bold ${dark ? "text-background-50" : "text-foreground-950"}`}>{title}</h3>
          )}
          {subtitle && (
            <p className={`mt-2 text-sm leading-relaxed ${dark ? "text-background-50/70" : "text-foreground-600"}`}>
              {subtitle}
            </p>
          )}
        </div>
      )}

      {status === "success" ? (
        <div className={`mt-5 rounded-lg p-4 text-sm ${dark ? "bg-background-50/10" : "bg-accent-50"} `}>
          <p className={`flex items-center gap-2 font-medium ${dark ? "text-background-50" : "text-accent-800"}`}>
            <i className="ri-checkbox-circle-fill" /> 感谢订阅，内容更新后会第一时间推送到你的邮箱。
          </p>
        </div>
      ) : (
        <form data-readdy-form={id} onSubmit={handleSubmit} className="mt-5 space-y-3.5" noValidate>
          <div className={withMessage ? "" : "grid gap-3 sm:grid-cols-2"}>
            <div className="flex flex-col gap-1.5">
              <label htmlFor={`${id}-nick`} className={`text-xs font-medium ${dark ? "text-background-50/70" : "text-foreground-600"}`}>
                怎么称呼你
              </label>
              <input
                id={`${id}-nick`}
                name="nickname"
                type="text"
                maxLength={30}
                placeholder="你的昵称（可选）"
                className={`w-full rounded-md border bg-background-50 px-3 py-2.5 text-sm outline-none transition-colors ${
                  dark
                    ? "border-background-50/20 text-background-50 placeholder:text-background-50/40 focus:border-background-50/60"
                    : "border-background-300/60 text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400"
                }`}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor={`${id}-email`} className={`text-xs font-medium ${dark ? "text-background-50/70" : "text-foreground-600"}`}>
                常用邮箱 <span className="text-primary-600">*</span>
              </label>
              <input
                id={`${id}-email`}
                name="email"
                type="email"
                required
                placeholder="name@example.com"
                className={`w-full rounded-md border bg-background-50 px-3 py-2.5 text-sm outline-none transition-colors ${
                  dark
                    ? "border-background-50/20 text-background-50 placeholder:text-background-50/40 focus:border-background-50/60"
                    : "border-background-300/60 text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400"
                }`}
              />
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor={`${id}-interest`} className={`text-xs font-medium ${dark ? "text-background-50/70" : "text-foreground-600"}`}>
              感兴趣的内容
            </label>
            <select
              id={`${id}-interest`}
              name="interest"
              defaultValue="all"
              className={`w-full rounded-md border bg-background-50 px-3 py-2.5 text-sm outline-none transition-colors ${
                dark
                  ? "border-background-50/20 text-background-50 focus:border-background-50/60"
                  : "border-background-300/60 text-foreground-900 focus:border-primary-400"
              }`}
            >
              <option value="all">全部精选内容</option>
              {categories.map((c) => (
                <option key={c.slug} value={c.slug}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          {withMessage && (
            <div className="flex flex-col gap-1.5">
              <label htmlFor={`${id}-msg`} className={`text-xs font-medium ${dark ? "text-background-50/70" : "text-foreground-600"}`}>
                留言或选题建议
              </label>
              <textarea
                id={`${id}-msg`}
                name="message"
                maxLength={500}
                rows={4}
                placeholder="告诉我们你想读的内容…（最多 500 字）"
                className={`w-full resize-none rounded-md border bg-background-50 px-3 py-2.5 text-sm outline-none transition-colors ${
                  dark
                    ? "border-background-50/20 text-background-50 placeholder:text-background-50/40 focus:border-background-50/60"
                    : "border-background-300/60 text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400"
                }`}
              />
            </div>
          )}

          {/* 反垃圾字段 */}
          <div className="hp-guard" aria-hidden="true">
            <input type="text" name="company_alt" tabIndex={-1} autoComplete="off" readOnly aria-hidden="true" />
          </div>

          {status === "error" && formError && (
            <p className={`flex items-center gap-2 text-sm ${dark ? "text-amber-200" : "text-primary-700"}`} role="alert">
              <i className="ri-error-warning-line" /> {formError}
            </p>
          )}

          <button
            type="submit"
            className={`inline-flex w-full items-center justify-center whitespace-nowrap rounded-md px-5 py-3 text-sm font-medium transition-colors ${
              dark ? "bg-background-50 text-foreground-950 hover:bg-background-100" : "bg-primary-500 text-background-50 hover:bg-primary-600"
            }`}
          >
            提交{withMessage ? "留言" : "订阅"} <i className="ri-arrow-right-line ml-1.5" />
          </button>
          <p className={`text-xs ${dark ? "text-background-50/50" : "text-foreground-400"}`}>
            提交即表示同意接收精选内容推送，可随时退订。我们不会向第三方共享你的信息（联系邮箱：{site.contact.email}）。
          </p>
        </form>
      )}
    </div>
  );
}